import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ArrowLeft, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { convertDateToCT } from "../../../../utils/util";
import { AuthContext } from "../../../../context/authContext";
import { downloadShipmentImage, getInventoryLogs } from "../../../../reducers/warehouse.slice";
import { Button } from "../../../../components/ui/button";
import DataTable from "../../../../components/shared/DataTable";
import PageHeader from "../../../../components/shared/PageHeader";
import useMediaQuery from "../../../../components/shared/useMediaQuery";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";

const ViewInventoryDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { upcValue } = useParams();
  const { showToast } = useContext(AuthContext);
  const [showLoader, setShowLoader] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [openImagesDialog, setOpenImagesDialog] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const isMobile = useMediaQuery("(max-width: 640px)");

  useEffect(() => { setCurrentImageIdx(0); }, [selectedImages]);

  useEffect(() => {
    setShowLoader(true);
    dispatch(getInventoryLogs(upcValue)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const formattedOrder = res?.payload?.map((item) => ({
        ...item,
        createdDate: convertDateToCT(item?.created_at, "MMMM D, YYYY hh:mm A"),
      }));
      setFilteredData(formattedOrder);
      setShowLoader(false);
    });
  }, []);

  const getImagesByID = (rowId) => {
    setShowLoader(true);
    dispatch(downloadShipmentImage(rowId)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setSelectedImages(res?.payload);
      setOpenImagesDialog(true);
      setShowLoader(false);
    });
  };

  const paginatedRows = filteredData.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const columns = [
    { key: "createdDate", label: "Time Stamp", width: 180 },
    {
      key: "status",
      label: "Type",
      width: 100,
      render: (row) => (
        <span
          style={{
            color:
              row?.status === "DAMAGED"
                ? "#FF3D00"
                : row?.status === "GOOD"
                ? "#4CAF50"
                : "inherit",
            fontWeight: 600,
          }}
        >
          {row?.status}
        </span>
      ),
    },
    {
      key: "inboundOutbound",
      label: "Inbound / Outbound",
      width: 150,
      render: (row) => (
        <span>{row?.adjustment_type === "ADD" ? "Inbound" : "Outbound"}</span>
      ),
    },
    { key: "quantity", label: "QTY", width: 70, align: "center" },
    { key: "serial_no", label: "Serial No.", width: 110 },
    {
      key: "change_type",
      label: "Change Type",
      width: 140,
      render: (row) => (
        <span>{row?.change_type === "MANUAL" ? "Manual Adjustment" : "Scan"}</span>
      ),
    },
    { key: "updated_by", label: "Updated By", width: 120 },
    { key: "notes", label: "Notes", width: 140 },
    {
      key: "action",
      label: "",
      width: 60,
      align: "center",
      render: (row) =>
        row?.status === "DAMAGED" ? (
          <Button
            variant="ghost"
            size="icon-sm"
            title="View images"
            onClick={() => getImagesByID(row?.id)}
          >
            <Images size={16} />
          </Button>
        ) : null,
    },
  ];

  const productName = filteredData[0]?.item_name;
  const productUpc = filteredData[0]?.upc;

  return (
    <div style={{ width: "100%" }}>
      {/* Back + header */}
      <button
        onClick={() => navigate(-1)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "#ffffff85",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
          fontSize: 14,
          marginBottom: 12,
        }}
      >
        <ArrowLeft size={14} /> Back
      </button>

      <PageHeader title="Product Details" />

      {/* Product summary card */}
      {(productName || productUpc) && (
        <div
          style={{
            backgroundColor: "#101010",
            borderRadius: 5,
            padding: "10px 16px",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 16,
            alignItems: isMobile ? "flex-start" : "center",
            justifyContent: "space-between",
            marginTop: 12,
            marginBottom: 16,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
              <span style={{ color: "#FFFFFF99", fontSize: 13 }}>Name:</span>
              <span style={{ fontSize: 13, textAlign: "right" }}>{productName}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
              <span style={{ color: "#FFFFFF99", fontSize: 13 }}>UPC:</span>
              <span style={{ fontSize: 13, color: "white" }}># {productUpc}</span>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <div style={{ marginBottom: 8, fontSize: 15, fontWeight: 600 }}>Stock Log</div>
        <DataTable
          columns={columns}
          rows={paginatedRows}
          totalRows={filteredData.length}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={(n) => setCurrentPage(n)}
          onRowsPerPageChange={(n) => { setRowsPerPage(n); setCurrentPage(0); }}
          loading={showLoader}
          minWidth={900}
        />
      </div>

      {/* Image carousel dialog */}
      <Dialog open={openImagesDialog} onOpenChange={() => setOpenImagesDialog(false)}>
        <DialogContent style={{ maxWidth: isMobile ? "95vw" : 520 }}>
          <DialogHeader>
            <DialogTitle>Images</DialogTitle>
          </DialogHeader>
          {selectedImages.length > 0 ? (
            <>
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: isMobile ? 180 : 300,
                  padding: "0 44px",
                }}
              >
                <button
                  onClick={() =>
                    setCurrentImageIdx((prev) =>
                      prev > 0 ? prev - 1 : selectedImages.length - 1
                    )
                  }
                  disabled={selectedImages.length <= 1}
                  style={{
                    position: "absolute",
                    left: 0,
                    background: "none",
                    border: "none",
                    cursor: selectedImages.length <= 1 ? "default" : "pointer",
                    padding: 4,
                    color: "white",
                    opacity: selectedImages.length <= 1 ? 0.3 : 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <ChevronLeft size={28} />
                </button>
                <img
                  src={`data:${selectedImages[currentImageIdx].content_type};base64,${selectedImages[currentImageIdx].document_bytes}`}
                  alt={`Item image ${currentImageIdx + 1}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: isMobile ? 180 : 350,
                    borderRadius: 8,
                    objectFit: "contain",
                    display: "block",
                  }}
                />
                <button
                  onClick={() =>
                    setCurrentImageIdx((prev) =>
                      prev < selectedImages.length - 1 ? prev + 1 : 0
                    )
                  }
                  disabled={selectedImages.length <= 1}
                  style={{
                    position: "absolute",
                    right: 0,
                    background: "none",
                    border: "none",
                    cursor: selectedImages.length <= 1 ? "default" : "pointer",
                    padding: 4,
                    color: "white",
                    opacity: selectedImages.length <= 1 ? 0.3 : 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <ChevronRight size={28} />
                </button>
              </div>
              <div style={{ textAlign: "center", marginTop: 8, fontSize: 13, color: "#888" }}>
                {currentImageIdx + 1} / {selectedImages.length}
              </div>
            </>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: isMobile ? 180 : 300,
                color: "#888",
              }}
            >
              No image available
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ViewInventoryDetail;
